import { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import PendingOrdersBadge from '@/components/PendingOrdersBadge';

export default function ConnectionStatus({ onSync, lastSync }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleConnectionChange = (event) => {
      setIsOnline(event.detail.online);
    };

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('connectionChange', handleConnectionChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('connectionChange', handleConnectionChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSync = async () => {
    if (!isOnline || !onSync) return;
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  };

  const formatLastSync = (dateString) => {
    if (!dateString) return 'Mai';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    return `${diffDays} giorni fa`;
  };

  return (
    <div className="flex items-center gap-3">
      {/* Connection Status Badge */}
      <Badge 
        className={`flex items-center gap-1.5 ${
          isOnline 
            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
            : 'bg-red-500/20 text-red-400 border-red-500/30'
        }`}
        variant="outline"
      >
        {isOnline ? (
          <>
            <Wifi className="w-3 h-3" />
            <span className="text-xs">Online</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3 h-3" />
            <span className="text-xs">Offline</span>
          </>
        )}
      </Badge>

      {/* Pending offline orders badge (clickable → manual retry) */}
      <PendingOrdersBadge />

      {/* Last Sync Info */}
      {lastSync && (
        <span className="text-xs text-zinc-500 hidden sm:inline">
          Sync: {formatLastSync(lastSync)}
        </span>
      )}

      {/* Sync Button */}
      {isOnline && onSync && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="h-7 px-2 text-zinc-400 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
        </Button>
      )}
    </div>
  );
}
